import React from 'react';
import PropTypes from 'prop-types';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import { useRequestData } from 'wsm-common-data';
import { badgePropTypes, badgeDefaultProps } from '../types/badgeProps';

const BadgeContent = ({
	badgeClasses,
	dataAttributes,
	disclaimer,
	href,
	imageAlt,
	imageSrc,
	imageTitle,
	target,
	tooltip,
	toggleOpenModal
}) => {
	const { deviceType } = useRequestData();
	const isMobile = deviceType === 'MOBILE';
	const badgeClassNames = badgeClasses.join(' ');
	// image element needs a height/width set for carfax svg logo to render correctly
	const isCarfax = badgeClassNames.includes('carfax');

	// WIS sends keys un-prefixed; consumers add `data-` (badge/Controller.groovy:36-41).
	const dataProps = Object.fromEntries(
		Object.entries(dataAttributes).map(([key, value]) => [
			key.startsWith('data-') ? key : `data-${key}`,
			value
		])
	);

	const hasTooltip = tooltip?.trim() && !isMobile;
	const hasDisclaimer = disclaimer?.trim();
	const hasHref = !!href;

	const ariaLabel = hasTooltip ? tooltip : imageAlt;

	const tooltipMarkup = (
		<Popover id={`popover-${imageSrc}`}>
			{/* eslint-disable-next-line react/no-danger */}
			<span dangerouslySetInnerHTML={{ __html: tooltip }} />
		</Popover>
	);

	// Base image element
	const elementContent = (
		<img
			key={imageSrc}
			className="m-0 p-0"
			src={imageSrc}
			alt={imageAlt}
			width={isCarfax ? '75px' : undefined}
			height={isCarfax ? 'auto' : undefined}
			title={imageTitle}
		/>
	);

	// Helper functions for wrapping elements
	const wrapInLink = (content, linkHref, linkTarget, className, label) => (
		<a
			href={linkHref.replace(/&amp;/g, '&')}
			target={linkTarget}
			className={className}
			rel={linkHref.startsWith('http') ? 'nofollow' : undefined}
			aria-label={label}
			{...dataProps}
		>
			{content}
		</a>
	);

	const wrapInButton = (content, onClick, label) => (
		<button
			type="button"
			className="text-link text-decoration-underline text-link-muted"
			onClick={onClick}
			aria-label={label}
			{...dataProps}
		>
			{content}
		</button>
	);

	const wrapInFocusableDiv = (content, label) => (
		<div
			// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
			tabIndex="0"
			className="d-inline-block"
			role="img"
			aria-label={label}
			{...dataProps}
		>
			{content}
		</div>
	);

	const getInteractiveWrapper = (content) => {
		if (hasHref)
			return wrapInLink(
				content,
				href,
				target,
				badgeClassNames,
				ariaLabel
			);
		if (hasDisclaimer)
			return wrapInButton(content, toggleOpenModal, ariaLabel);
		if (hasTooltip) return wrapInFocusableDiv(content, ariaLabel);
		return content;
	};

	const wrapWithTooltip = (content) =>
		hasTooltip ? (
			<OverlayTrigger
				placement="top"
				overlay={tooltipMarkup}
				trigger={['hover']}
			>
				{content}
			</OverlayTrigger>
		) : (
			content
		);

	// Apply wrappers
	const interactiveContent = getInteractiveWrapper(elementContent);
	const finalContent = wrapWithTooltip(interactiveContent);

	return finalContent;
};

BadgeContent.propTypes = {
	...badgePropTypes,
	toggleOpenModal: PropTypes.func.isRequired
};

BadgeContent.defaultProps = badgeDefaultProps;

export default BadgeContent;
